/**
 * <cart-recommendations>
 * ---------------------------------------------------------------------------
 * Dynamic cart-drawer recommendations for the Vivi&Co (Prestige) theme.
 *
 * Runs a fallback chain keyed to the most recently added product:
 *   1. complementary  (your Search & Discovery "complete the look" pairings)
 *   2. related        (Shopify's automatic algorithm)
 *   3. static         (the hand-picked list rendered server-side in the drawer)
 *
 * It fetches the lightweight `cart-recommendations` section per intent via the
 * Product Recommendations API, so it never re-renders the whole drawer. The
 * static list is rendered visible by default, so if JS fails the shopper still
 * sees the merchant's hand-picked products (graceful degradation).
 *
 * Self-contained vanilla JS — no theme.js edits, survives theme updates.
 */
(function () {
  if (customElements.get('cart-recommendations')) return;

  class CartRecommendations extends HTMLElement {
    static get observedAttributes() {
      return ['product'];
    }

    get productId() {
      return this.getAttribute('product');
    }

    get sectionId() {
      return this.getAttribute('section') || 'cart-recommendations';
    }

    get limit() {
      return this.getAttribute('limit') || '4';
    }

    get intents() {
      return (this.getAttribute('intents') || 'complementary,related')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
    }

    connectedCallback() {
      this.maybeLoad();
    }

    attributeChangedCallback(name) {
      if (name === 'product') this.maybeLoad();
    }

    // De-dupe: connectedCallback + attributeChangedCallback can both fire on
    // upgrade, and the drawer re-renders on every cart change. Only load once
    // per distinct anchor product.
    maybeLoad() {
      var product = this.productId;
      if (!product || product === this._loadedFor) return;
      this._loadedFor = product;
      this.load(product);
    }

    async load(product) {
      var target = this.querySelector('[data-reco-target]');
      var fallback = this.querySelector('[data-reco-fallback]');
      var header = this.querySelector('[data-reco-header]');
      if (!target) return;

      for (var i = 0; i < this.intents.length; i++) {
        // Bail if a newer load superseded this one (anchor changed mid-flight).
        if (this._loadedFor !== product) return;

        var intent = this.intents[i];
        try {
          var markup = await this.fetchIntent(product, intent);
          if (this._loadedFor !== product) return;

          if (markup) {
            target.innerHTML = markup;
            target.hidden = false;
            if (header) header.hidden = false;
            if (fallback) fallback.hidden = true;
            this.dispatchEvent(new CustomEvent('cart-recommendations:loaded', {
              bubbles: true,
              detail: { intent: intent, product: product }
            }));
            return;
          }
        } catch (e) {
          /* try the next intent */
        }
      }

      // Nothing dynamic — leave the static fallback as it was rendered (visible
      // if the merchant configured one, otherwise nothing shows).
    }

    async fetchIntent(product, intent) {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var url = root + 'recommendations/products'
        + '?product_id=' + encodeURIComponent(product)
        + '&limit=' + encodeURIComponent(this.limit)
        + '&intent=' + encodeURIComponent(intent)
        + '&section_id=' + encodeURIComponent(this.sectionId);

      var response = await fetch(url);
      if (!response.ok) throw new Error('cart-recommendations: ' + response.status);

      var text = await response.text();
      var doc = new DOMParser().parseFromString(text, 'text/html');

      // The section outputs bare cards; if none came back, treat as empty.
      if (!doc.querySelector('.horizontal-product-card')) return null;

      // The Section Rendering API may wrap output in a `.shopify-section`
      // div — unwrap it so we inject only the cards.
      var wrapper = doc.querySelector('.shopify-section') || doc.body;
      return wrapper.innerHTML.trim() || null;
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();
